import React from 'react';
import styled from 'styled-components';
import { Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import { useColorTheme } from '@hooks';
import {
  useUploadStatus,
  useUploadStatusDDQTemplateGeneration,
  useUploadStatusTemplateGeneration,
} from '@hooks/features/useUploadStatus';
import { createTimeInstance } from '@utils/time';

const Wrapper = styled.div`
  padding-top: 16px;
`;

const TableWrapper = styled.div`
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const HeaderRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.text};
`;

const HeaderCell = styled.th`
  padding: 8px;
  text-align: left;
  font-size: 12px;
  line-height: 14px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ theme }) => theme.themeColors.text};
`;

const StatusHeaderCell = styled(HeaderCell)`
  width: 200px;
  text-align: right;
`;

const DocRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const DocCell = styled.td`
  padding: 8px;
  vertical-align: middle;
`;

const StatusCell = styled(DocCell)`
  text-align: right;
  width: 200px;
`;

const ProgressBarWrapper = styled.div`
  width: 120px;
  height: 15px;
  background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 1px;
  transition: width 0.3s ease-in-out;
  width: ${({ $percent }) => $percent}%;
`;

const ProgressBarTextWrapper = styled.div`
  padding-left: 8px;
  width: 44px;
  text-align: right;
  overflow: hidden;
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error || '#d32f2f'};
`;

function expectedTimeToUpload(fileSizeBytes, fileName = '', filter = null) {
  if (filter === 'template_generation') {
    return 1000 * 60 * 20;
  }

  const fileSizeMB = fileSizeBytes / 1024 / 1024;
  const isExcelFile =
    fileName &&
    (fileName.toLowerCase().endsWith('.xls') ||
      fileName.toLowerCase().endsWith('.xlsx') ||
      fileName.toLowerCase().endsWith('.xlsm') ||
      fileName.toLowerCase().endsWith('.xlsb'));

  if (isExcelFile) {
    const minTime = 10;
    const maxTime = 60;
    const maxSize = 100;

    if (fileSizeMB <= 0) return 1000 * 60 * minTime;
    if (fileSizeMB >= maxSize) return 1000 * 60 * maxTime;

    const interpolatedTime = minTime + (fileSizeMB / maxSize) * (maxTime - minTime);
    return 1000 * 60 * interpolatedTime;
  } else {
    const minTime = 2;
    const maxTime = 60;
    const minSize = 1;
    const maxSize = 8;

    if (fileSizeMB < minSize) return 1000 * 60 * minTime;
    if (fileSizeMB > maxSize) return 1000 * 60 * maxTime;

    const interpolatedTime = minTime + ((fileSizeMB - minSize) / (maxSize - minSize)) * (maxTime - minTime);
    return 1000 * 60 * interpolatedTime;
  }
}

function capProgress(progress, max) {
  return progress > max ? max : progress;
}

function calculateProgress(fileSizeBytes, uploadFinishedAt, fileName = '', filter = null) {
  if (!fileSizeBytes || !uploadFinishedAt) return 1;

  const timeUploading = createTimeInstance();
  const timeUploadFinished = createTimeInstance(uploadFinishedAt);
  const timeElapsed = timeUploading.diff(timeUploadFinished);
  const expectedTime = expectedTimeToUpload(fileSizeBytes, fileName, filter);
  const progress = (timeElapsed / expectedTime) * 100;
  return Math.round(capProgress(progress, 85));
}

const ProgressBar = ({ value = 0 }) => {
  return (
    <Container row verticallyCenter>
      <ProgressBarWrapper>
        <ProgressBarFill $percent={value} />
      </ProgressBarWrapper>
      <ProgressBarTextWrapper>
        <Body5 isLowContrast>{value}%</Body5>
      </ProgressBarTextWrapper>
    </Container>
  );
};

const PendingStatus = ({ doc, filter }) => {
  const [progress, setProgress] = React.useState(
    calculateProgress(doc.file_size_bytes, doc.upload_finished_at, doc.title, filter)
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress(calculateProgress(doc.file_size_bytes, doc.upload_finished_at, doc.title, filter));
    }, 3000);
    return () => clearInterval(interval);
  }, [doc.file_size_bytes, doc.upload_finished_at, doc.title, filter]);

  return (
    <Container row verticallyCenter alignRight>
      <Container right={12}>
        <Body5 isLowContrast>Processing...</Body5>
      </Container>
      <ProgressBar value={progress} />
    </Container>
  );
};

const CompletedStatus = () => {
  return (
    <Container row verticallyCenter alignRight>
      <Container>
        <Body5>Complete</Body5>
      </Container>
    </Container>
  );
};

const ErroredStatus = () => {
  return (
    <Container row verticallyCenter alignRight>
      <ErrorText>Error</ErrorText>
    </Container>
  );
};

const StatusDisplay = ({ doc, filter }) => {
  if (doc._status === 'pending') return <PendingStatus doc={doc} filter={filter} />;
  if (doc._status === 'errored') return <ErroredStatus />;
  return <CompletedStatus />;
};

function flattenAndSort(data) {
  if (!data) return [];

  const { completed = [], pending = [], errored = [] } = data;

  const tagged = [
    ...completed.map((d) => ({ ...d, _status: 'completed' })),
    ...pending.map((d) => ({ ...d, _status: 'pending' })),
    ...errored.map((d) => ({ ...d, _status: 'errored' })),
  ];

  tagged.sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });

  return tagged;
}

const DocList = ({ docs, filter }) => {
  if (!docs || docs.length === 0) return null;

  return (
    <TableWrapper>
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell>Upload Progress</HeaderCell>
            <StatusHeaderCell>Status</StatusHeaderCell>
          </HeaderRow>
        </thead>
        <tbody>
          {docs.map((doc) => (
            <DocRow key={doc.id || doc.doc_id}>
              <DocCell>
                <Body5>{doc.title}</Body5>
              </DocCell>
              <StatusCell>
                <StatusDisplay doc={doc} filter={filter} />
              </StatusCell>
            </DocRow>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

const empty = {
  completed: [],
  pending: [],
  errored: [],
};

const StatusContent = ({ isInitializing, data, filter, hideIfNoDocs = false }) => {
  const colorTheme = useColorTheme();
  const docs = React.useMemo(() => flattenAndSort(data || empty), [data]);

  if (hideIfNoDocs && (isInitializing || docs.length === 0)) {
    return null;
  }

  return (
    <Wrapper>
      {/* <Container bottom={16}>
        <Body5 uppercase bold>
          Upload Progress
        </Body5>
      </Container> */}

      {isInitializing ? (
        <Container row verticallyCenter>
          <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
          <Container left={8}>
            <Body5>Checking document uploads...</Body5>
          </Container>
        </Container>
      ) : null}

      {!isInitializing && docs.length === 0 ? (
        <Container bottom={16}>
          <Body5>No uploads in progress</Body5>
        </Container>
      ) : null}

      {docs.length > 0 ? <DocList docs={docs} filter={filter} /> : null}
    </Wrapper>
  );
};

export function DocumentUploadStatusFlat({ filter }) {
  const { isInitializing, data } = useUploadStatus(filter);
  return <StatusContent isInitializing={isInitializing} data={data} filter={filter} />;
}

export function DocumentUploadStatusFlatTemplateGeneration() {
  const { isInitializing, data } = useUploadStatusTemplateGeneration();
  return <StatusContent isInitializing={isInitializing} data={data} filter="template_generation" />;
}

export function DocumentUploadStatusFlatDDQTemplateGeneration({ hideIfNoDocs = false, onFileUploadComplete = null }) {
  const { isInitializing, data } = useUploadStatusDDQTemplateGeneration(onFileUploadComplete);
  return <StatusContent isInitializing={isInitializing} data={data} hideIfNoDocs={hideIfNoDocs} />;
}
